// Home page, laid out like the prototype's dashboard (Agency_Command_Center_v131.html,
// renderDashboard): greeting, five cards, recent calls, active quoting, and the management
// and priority panels. The figures come from the API with the prototype's definitions.
import { Link } from 'react-router';
import { Banner, useQuery } from '@acc/web-shared';
import type { CallStatus, LifeStatus, PcStatus, QuoteType } from '@acc/domain';
import { shortDate, useAgency } from '../agency';
import { CallPill, QuotePill } from './pills';

interface Dash {
  today: string;
  unassignedIntake: number;
  pendingApprovals: number | null;
  missedApprovals: number | null;
  cards: { urgent: number; overdue: number; upcoming: number; scheduledRequotes: number; completedSales: number };
  recentCalls: Array<{ callId: string; customerName: string; reason: string; ownerMembershipId: string | null; status: CallStatus }>;
  activeQuotes: Array<{ opportunityId: string; customerName: string; type: QuoteType; pcStatus: PcStatus | null; lifeStatus: LifeStatus | null; ownerMembershipId: string | null; dueDate: string | null }>;
  quickLinks: Array<{ linkId: string; label: string; url: string | null }>;
  priorities: Array<{ kind: 'quote' | 'call'; id: string; text: string; date: string; ownerMembershipId: string | null }>;
}

const CARDS = [
  ['urgent', 'Urgent · next 3 days', '!', 'red', '/quotes?due=week'],
  ['overdue', 'Overdue', '◷', 'amber', '/quotes?due=overdue'],
  ['upcoming', 'Upcoming · 7 days', '▦', 'blue', '/quotes?due=week'],
  ['scheduledRequotes', 'Scheduled requotes', '↻', 'violet', '/follow-ups'],
  ['completedSales', 'Completed sales', '✓', 'green', '/sales'],
] as const;

export function Dashboard() {
  const a = useAgency();
  const q = useQuery<Dash>(a.path('/dashboard'));
  if (q.error) return <Banner tone="bad">{q.error.message}</Banner>;
  if (!q.data) return <p className="muted">Loading…</p>;
  const d = q.data;
  const first = (a.name(a.me.membershipId).split(/[\s@]/)[0] || 'Team');
  const greeting = `${new Date().getHours() < 12 ? 'Good Morning' : 'Good Afternoon'}, ${first}.`;
  const owner = (id: string | null) => (id ? a.name(id) : 'Unassigned');
  return (
    <div className="home">
      <div className="page-head">
        <div>
          <div className="eyebrow">Agency overview</div>
          <h1>{greeting}</h1>
          <p className="sub">One place to manage every opportunity and follow-up. Today is {shortDate(d.today)}.</p>
        </div>
        <Link to={`${a.base}/intake/new`} className="btn primary">+ New Quote Request</Link>
      </div>

      <div className="home-cards">
        {CARDS.map(([key, label, icon, tone, to]) => (
          <Link key={key} to={`${a.base}${to}`} className="home-card">
            <span className={`home-icon ${tone}`} aria-hidden="true">{icon}</span>
            <span><b>{d.cards[key]}</b><span>{label}</span></span>
          </Link>
        ))}
      </div>

      <div className="home-grid">
        <div>
          <section className="panel" aria-labelledby="h-calls">
            <div className="panelhead">
              <h2 id="h-calls"><span aria-hidden="true">☎ </span>Recent Phone Calls</h2>
              <Link to={`${a.base}/calls/new`} className="btn">+ Log Call</Link>
            </div>
            {d.recentCalls.length ? (
              <table className="data">
                <thead><tr><th>Customer</th><th>Reason</th><th>Owner</th><th>Status</th></tr></thead>
                <tbody>
                  {d.recentCalls.map((c) => (
                    <tr key={c.callId}>
                      <td><Link to={`${a.base}/calls/${c.callId}`}>{c.customerName}</Link></td>
                      <td>{c.reason}</td>
                      <td>{owner(c.ownerMembershipId)}</td>
                      <td><CallPill s={c.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="empty">No calls logged yet.</div>}
          </section>

          <section className="panel" aria-labelledby="h-quoting">
            <div className="panelhead">
              <h2 id="h-quoting"><span aria-hidden="true">▤ </span>Active Quoting</h2>
              <Link to={`${a.base}/quotes`} className="btn">View All →</Link>
            </div>
            {d.activeQuotes.length ? (
              <table className="data">
                <thead><tr><th>Customer</th><th>Type</th><th>P/C Status</th><th>Life Status</th><th>Assigned To</th><th>Due Date</th></tr></thead>
                <tbody>
                  {d.activeQuotes.map((o) => (
                    <tr key={o.opportunityId}>
                      <td><Link to={`${a.base}/quotes/${o.opportunityId}`}>{o.customerName}</Link></td>
                      <td><QuotePill kind="type" value={o.type} /></td>
                      <td><QuotePill kind="pc" value={o.pcStatus} /></td>
                      <td><QuotePill kind="life" value={o.lifeStatus} /></td>
                      <td>{owner(o.ownerMembershipId)}</td>
                      <td className={o.dueDate && o.dueDate < d.today ? 'due-past' : ''}>{shortDate(o.dueDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="empty">No quotes in progress.</div>}
          </section>
        </div>

        <div>
          {a.manager && (
            <section className="panel" aria-labelledby="h-approvals">
              <div className="panelhead">
                <h2 id="h-approvals"><span aria-hidden="true">↻ </span>Missed Quote Approvals</h2>
                <Link to={`${a.base}/approvals`} className="btn small">Review</Link>
              </div>
              <p>{d.missedApprovals ? `${d.missedApprovals} quote(s) awaiting manager approval.` : 'All caught up.'}</p>
              {(d.pendingApprovals ?? 0) > (d.missedApprovals ?? 0) && <p className="muted">{d.pendingApprovals! - d.missedApprovals!} early-reopening request(s) waiting too.</p>}
            </section>
          )}
          {a.manager && (
            <section className="panel" aria-labelledby="h-intake">
              <div className="panelhead">
                <h2 id="h-intake"><span aria-hidden="true">✉ </span>Quote Intake</h2>
                <Link to={`${a.base}/intake`} className="btn small">Review</Link>
              </div>
              <p>{d.unassignedIntake ? `${d.unassignedIntake} quote request(s) awaiting assignment.` : 'All caught up.'}</p>
            </section>
          )}
          <section className="panel" aria-labelledby="h-priorities">
            <div className="panelhead">
              <h2 id="h-priorities"><span aria-hidden="true">✓ </span>Today's Priorities</h2>
              <span className="pillcount">{d.priorities.length} to review</span>
            </div>
            {d.priorities.length ? d.priorities.map((p) => (
              <div key={`${p.kind}${p.id}`} className="task">
                <span className="dot" aria-hidden="true">●</span>
                <div>
                  <Link to={`${a.base}/${p.kind === 'quote' ? 'quotes' : 'calls'}/${p.id}`}>{p.text}</Link>
                  <div className="muted">Due {shortDate(p.date)}{a.manager && ` · Assigned to ${owner(p.ownerMembershipId)}`}</div>
                </div>
              </div>
            )) : <div className="empty">Nothing urgent right now.</div>}
          </section>
          <section className="panel" aria-labelledby="h-links">
            <div className="panelhead">
              <h2 id="h-links"><span aria-hidden="true">↗ </span>Agency Quick Links</h2>
              <Link to={`${a.base}/resources`} className="btn small">{a.manager ? 'Manage' : 'All'}<span className="sr-only"> agency resources</span></Link>
            </div>
            {d.quickLinks.length ? d.quickLinks.map((l) => (
              <div key={l.linkId} className="task">
                <span aria-hidden="true">↗</span>
                {l.url ? <a href={l.url} target="_blank" rel="noopener noreferrer">{l.label}<span className="sr-only"> (opens in a new tab)</span></a> : <span>{l.label} <span className="muted">(link not configured)</span></span>}
              </div>
            )) : <div className="empty">{a.manager ? "Add your agency's portals and documents under Resources." : 'No quick links yet.'}</div>}
          </section>
        </div>
      </div>
    </div>
  );
}
